const ObjectChoiceModal = ({
  onClose,
  title,
  children,
  titleId = 'object-choice-title',
  stackClassName = 'z-[100]',
}) => (
  <div
    className={`fixed inset-0 ${stackClassName} flex items-center justify-center p-4 bg-black/20`}
    role="dialog"
    aria-modal="true"
    aria-labelledby={titleId}
  >
    <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col backdrop-blur-xl backdrop-saturate-150 border-white/50 bg-white/35 hover:border-white/70">
      <div className="p-2 flex items-center justify-end gap-3 absolute top-0 right-0">
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded text-gray-500 transition-colors"
          aria-label="Close object selection popup"
          title="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="flex items-start justify-center gap-3 px-8 pt-4">
        <h2
          id={titleId}
          className="flex min-h-[2.5rem] items-center justify-center text-center text-lg lao-mn text-primary-300"
        >
          {title}
        </h2>
      </div>

      {children}
    </div>
  </div>
);

/** Same shell as ObjectChoiceModal; used before starting remove_object AI inference. */
export function RemoveObjectConfirmModal({ onClose, onConfirm, confirmLoading }) {
  return (
    <ObjectChoiceModal
      onClose={onClose}
      title={
        <>
          <span className="sr-only">Remove object</span>
          <svg className="w-10 h-10 shrink-0" viewBox="0 0 39 46" fill="none" aria-hidden="true">
            <path
              d="M33.25 15.75L31.29 35.3453C30.9937 38.3157 30.8467 39.7997 30.17 40.922C29.5765 41.9099 28.7037 42.7 27.6617 43.1923C26.4787 43.75 24.99 43.75 22.0033 43.75H16.4967C13.5123 43.75 12.0213 43.75 10.8383 43.19C9.79544 42.698 8.92176 41.908 8.32767 40.9197C7.65567 39.7997 7.50633 38.3157 7.20767 35.3453L5.25 15.75M22.75 30.9167V19.25M15.75 30.9167V19.25M1.75 9.91667H12.5183M12.5183 9.91667L13.419 3.682C13.6803 2.548 14.623 1.75 15.7057 1.75H22.7943C23.877 1.75 24.8173 2.548 25.081 3.682L25.9817 9.91667M12.5183 9.91667H25.9817M25.9817 9.91667H36.75"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </>
      }
      titleId="remove-object-confirm-title"
      stackClassName="z-[105]"
    >
      <div className="px-4 pb-4 pt-2 flex flex-col gap-4">
        <p className="text-sm text-gray-600 text-center leading-relaxed">
          Are you sure you want to delete this object? If you want to change it, you can edit it using other
          functions. Deleted objects can be restored using the history.
        </p>
        <div className="flex flex-row gap-2 w-full">
          <button
            type="button"
            disabled={confirmLoading}
            onClick={onClose}
            className="flex flex-1 flex-row justify-center items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={confirmLoading}
            onClick={onConfirm}
            className="flex flex-1 flex-row justify-center items-center gap-2 rounded-full border border-gray-800 bg-gray-800 px-4 py-3 text-sm font-medium text-white hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {confirmLoading ? 'Starting…' : 'Confirm'}
          </button>
        </div>
      </div>
    </ObjectChoiceModal>
  );
}

/** Shown before deleting a saved render from the library or recent strip. */
export function DeleteRenderConfirmModal({ onClose, onConfirm, confirmLoading }) {
  return (
    <ObjectChoiceModal
      onClose={onClose}
      title={
        <>
          <span className="sr-only">Delete render</span>
          <svg className="w-10 h-10 shrink-0" viewBox="0 0 39 46" fill="none" aria-hidden="true">
            <path
              d="M33.25 15.75L31.29 35.3453C30.9937 38.3157 30.8467 39.7997 30.17 40.922C29.5765 41.9099 28.7037 42.7 27.6617 43.1923C26.4787 43.75 24.99 43.75 22.0033 43.75H16.4967C13.5123 43.75 12.0213 43.75 10.8383 43.19C9.79544 42.698 8.92176 41.908 8.32767 40.9197C7.65567 39.7997 7.50633 38.3157 7.20767 35.3453L5.25 15.75M22.75 30.9167V19.25M15.75 30.9167V19.25M1.75 9.91667H12.5183M12.5183 9.91667L13.419 3.682C13.6803 2.548 14.623 1.75 15.7057 1.75H22.7943C23.877 1.75 24.8173 2.548 25.081 3.682L25.9817 9.91667M12.5183 9.91667H25.9817M25.9817 9.91667H36.75"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </>
      }
      titleId="delete-render-confirm-title"
      stackClassName="z-[125]"
    >
      <div className="px-4 pb-4 pt-2 flex flex-col gap-4">
        <p className="text-sm text-gray-600 text-center leading-relaxed">
          Are you sure you want to delete this saved render? This action cannot be undone.
        </p>
        <div className="flex flex-row gap-2 w-full">
          <button
            type="button"
            disabled={confirmLoading}
            onClick={onClose}
            className="flex flex-1 flex-row justify-center items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={confirmLoading}
            onClick={onConfirm}
            className="flex flex-1 flex-row justify-center items-center gap-2 rounded-full border border-gray-800 bg-gray-800 px-4 py-3 text-sm font-medium text-white hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {confirmLoading ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </ObjectChoiceModal>
  );
}

/** Shown before committing a curated model placement onto the canvas (AI incorporate flow). */
export function CuratedPlacementConfirmModal({ onClose, onConfirm }) {
  return (
    <ObjectChoiceModal
      onClose={onClose}
      title={
        <>
          <span className="sr-only">Update model</span>
          <svg
            className="h-12 w-12 shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M24 15.254V26.254M24 32.746V31.746M42 24C42 26.3638 41.5344 28.7044 40.6298 30.8883C39.7252 33.0722 38.3994 35.0565 36.7279 36.7279C35.0565 38.3994 33.0722 39.7252 30.8883 40.6298C28.7044 41.5344 26.3638 42 24 42C21.6362 42 19.2956 41.5344 17.1117 40.6298C14.9278 39.7252 12.9435 38.3994 11.2721 36.7279C9.60062 35.0565 8.27475 33.0722 7.37017 30.8883C6.46558 28.7044 6 26.3638 6 24C6 19.2261 7.89642 14.6477 11.2721 11.2721C14.6477 7.89642 19.2261 6 24 6C28.7739 6 33.3523 7.89642 36.7279 11.2721C40.1036 14.6477 42 19.2261 42 24Z"
              stroke="#2A3040"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </>
      }
      titleId="curated-placement-confirm-title"
      stackClassName="z-[105]"
    >
      <div className="flex flex-col gap-4 px-6 pb-6 pt-2">
        <p className="text-sm leading-relaxed text-gray-600 text-center">
          The system will utilize AI to incorporate models into the image content. These models will be
          identified as objects and recognized by the system. They will subsequently be added to the detected
          object database, allowing you to edit, refine, or regenerate them as needed in the Object section.
        </p>
        <div className="flex w-fit mx-auto flex-row gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="flex flex-1 flex-row items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-primary-300 transition-colors hover:border-primary-300 hover:bg-primary-500 hover:text-primary-300"
          >
            <svg class="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
              <path d="M8.5625 8.5625L4.5625 4.5625M8.5625 4.5625L4.5625 8.5625M6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53844 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58655 1.01922 8.8586C0.717693 8.13065 0.562499 7.35043 0.562499 6.5625C0.562499 5.77457 0.717693 4.99435 1.01922 4.2664C1.32075 3.53845 1.76271 2.87701 2.31986 2.31986C2.87701 1.76271 3.53844 1.32075 4.2664 1.01922C4.99435 0.717694 5.77457 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625C12.5625 8.1538 11.9304 9.67992 10.8051 10.8051C9.67992 11.9304 8.1538 12.5625 6.5625 12.5625Z" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex flex-1 flex-row items-center justify-center gap-2 rounded-full border border-gray-800 bg-gray-800 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
          >
            <svg class="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
              <path d="M12.5625 6.5625C12.5625 7.35043 12.4073 8.13065 12.1058 8.8586C11.8043 9.58656 11.3623 10.248 10.8051 10.8051C10.248 11.3623 9.58656 11.8043 8.8586 12.1058C8.13065 12.4073 7.35043 12.5625 6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53845 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58656 1.01922 8.8586C0.717695 8.13065 0.5625 7.35043 0.5625 6.5625C0.5625 4.9712 1.19464 3.44508 2.31986 2.31986C3.44508 1.19464 4.9712 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625Z" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"></path><path d="M4.34033 6.98445L5.34367 8.13178C5.40969 8.20734 5.49192 8.26701 5.58422 8.30636C5.67652 8.34571 5.77651 8.36372 5.87674 8.35904C5.97697 8.35436 6.07485 8.32712 6.16308 8.27934C6.25132 8.23157 6.32763 8.16449 6.38633 8.08312L8.78433 4.76245" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
            Confirm
          </button>
        </div>
      </div>
    </ObjectChoiceModal>
  );
}

/** Shown when generate_flythrough inference fails (preview or final). */
export function FlythroughGenerationFailedModal({ onClose, onRetry, retryLoading = false }) {
  return (
    <ObjectChoiceModal
      onClose={onClose}
      title={
        <>
          <span className="sr-only">Flythrough generation failed</span>
          <svg
            className="h-10 w-10 shrink-0 text-primary-300"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
          </svg>
        </>
      }
      titleId="flythrough-generation-failed-title"
      stackClassName="z-[125]"
    >
      <div className="flex flex-col gap-4 px-4 pb-4 pt-2">
        <p className="text-center text-sm leading-relaxed text-gray-600">
          An issue occurred during generation. You may retry or optimize your settings for improved
          results.
        </p>
        <div className="flex w-full flex-row gap-2">
          <button
            type="button"
            disabled={retryLoading}
            onClick={onClose}
            className="flex flex-1 flex-row items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-primary-300 transition-colors hover:border-primary-300 hover:bg-primary-500 hover:text-primary-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={retryLoading}
            onClick={onRetry}
            className="flex flex-1 flex-row items-center justify-center gap-2 rounded-full border border-gray-800 bg-gray-800 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {retryLoading ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      </div>
    </ObjectChoiceModal>
  );
}

export default ObjectChoiceModal;
